// WHAT: Debug component for diagnosing TimeTracQ workspace loading and data structure issues.
// WHY:  Provides developers and users with diagnostic information about database content and workspace state to troubleshoot loading problems.
// DOMAIN: Settings → TimeTracQ
// DEPENDENCIES: React, @tauri-apps/api/core, @/components/ui, lucide-react
// RISKS:
//   - NOTE: Uses `debug_database_content` Tauri command; failures will show error state.
//   - NOTE: Displays raw database content; sensitive data may be visible in debug output.

import { core } from "@tauri-apps/api";
import { Database, AlertCircle, Loader2, RefreshCw, Bug } from "lucide-react";
import { useState, useEffect } from "react";

import { Card, Button, Stack, Heading } from "@/components/ui";

/**
 * WHAT:
 *   Debug component for diagnosing TimeTracQ workspace loading and data structure issues.
 *
 * WHY:
 *   Provides developers and users with diagnostic information about database content and workspace state to troubleshoot loading problems.
 *
 * BEHAVIOR:
 *   - Automatically runs debug test on mount.
 *   - Invokes `debug_database_content` Tauri command to fetch database content.
 *   - Displays raw database content in JSON format.
 *   - Provides refresh button to re-run debug test.
 *   - Shows error state if debug command fails.
 *
 * RISKS:
 *   - NOTE: Uses `debug_database_content` Tauri command; failures will show error state.
 *   - NOTE: Displays raw database content; sensitive data may be visible in debug output.
 *
 * @returns {JSX.Element} TimeTracQ workspace debug component
 *
 */
// Reason: Component is used internally by TimeTracQSettingsView via routing, not directly imported

export function TimeTracQWorkspaceDebug() {
  const [debugData, setDebugData] = useState<null | {
    timestamp: string;
    content: Record<string, unknown>;
  }>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();

  /**
   * WHAT:
   *   Runs a debug test to fetch database content and diagnose workspace loading issues.
   *
   * WHY:
   *   To provide diagnostic information about the current database state and help identify loading problems.
   *
   * BEHAVIOR:
   *   - Invokes `debug_database_content` Tauri command.
   *   - Stores the result along with a timestamp in `debugData` state.
   *   - Handles errors by setting error state.
   *
   * RISKS:
   *   - WARN: If the Tauri command fails, the component will show an error state.
   *   - NOTE: Database content may contain sensitive data; displayed in raw JSON format.
   */
  /**
   * WHAT:
   *   Tests workspace loading by fetching database content using reliable debug command.
   *
   * WHY:
   *   Uses debug_database_content instead of workspace-specific commands that may be problematic,
   *   providing a working alternative for workspace diagnostics.
   *
   * SIDE EFFECTS:
   *   - Invokes debug_database_content command
   *   - Updates debug data state with database content
   *   - Adds timestamp for debugging session tracking
   *
   * RISKS:
   *   - NOTE: Uses generic database content instead of workspace-specific debug
   *   - WARN: May trigger database connection pool initialization
   *   - NOTE: Timestamp helps track when debug data was collected
   */
  const testWorkspaceLoading = async () => {
    try {
      setLoading(true);
      setError(undefined);

      // NOTE: Use the working database content debug instead of problematic workspace commands
      // WARN: This is a workaround - workspace-specific debug commands may not be available
      const databaseContent = await core.invoke<Record<string, unknown>>("debug_database_content");

      setDebugData({
        content: databaseContent,
        timestamp: new Date().toISOString(),
      });
    } catch (error_) {
      setError(error_ instanceof Error ? error_.message : "Debug failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void testWorkspaceLoading();
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="p-3 bg-purple-500/10 rounded-lg">
            <Bug className="w-6 h-6 text-purple-400" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              TimeTracQ Workspace Debug
            </Heading>
            <p className="text-slate-300">Debug workspace loading and data structure issues.</p>
          </Stack>
        </Stack>
      </Card>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <Button
          disabled={loading}
          onClick={() => {
            void testWorkspaceLoading();
          }}
          size="md"
          variant="secondary"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Testing...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Test Workspace Loading
            </>
          )}
        </Button>
      </div>

      {/* Debug Results */}
      {debugData ? (
        <div className="space-y-4">
          {/* Database Content */}
          <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
            <Stack direction="col" gap={4}>
              <Heading className="text-lg font-medium text-white flex items-center gap-2" level={3}>
                <Database className="w-5 h-5 text-slate-400" />
                Database Content (Working Method)
              </Heading>

              <div className="p-3 bg-slate-700/50 rounded-lg">
                <pre className="text-sm text-slate-300 overflow-x-auto">
                  {JSON.stringify(debugData.content, null, 2)}
                </pre>
              </div>
            </Stack>
          </Card>
        </div>
      ) : null}

      {/* Error Display */}
      {error ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300 text-sm font-medium">Debug Error: {error}</span>
          </Stack>
        </Card>
      ) : null}

      {/* Raw Debug Data */}
      {debugData ? (
        <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
          <Stack direction="col" gap={4}>
            <Heading className="text-lg font-medium text-white" level={3}>
              Raw Debug Data
            </Heading>
            <div className="p-3 bg-slate-700/50 rounded-lg">
              <pre className="text-sm text-slate-300 overflow-x-auto">
                {JSON.stringify(debugData, null, 2)}
              </pre>
            </div>
          </Stack>
        </Card>
      ) : null}
    </div>
  );
}
